import React, { useState, useMemo } from 'react';
import Button from '@app/react-common/components/button/Button';
import { Checkbox } from '@app/react-common/components/checkbox/Checkbox';
import Divider from '@app/react-common/components/divider/Divider';
import RadioButtonGroup, { RadioButtonOption } from '@app/react-common/components/radio-button-group/RadioButtonGroup';
import Tooltip from '@app/react-common/components/tooltip/Tooltip';
import { useAppContext } from '@app/react-common/AppContext';
import { SelectedMethodInfo, ConditionGroup, DisplayConditionsFormData } from '../BulkEditModal';
import { useConditionsAggregation } from '../hooks/useConditionsAggregation';
import { DialogConditionGroup, DialogCondition } from './types';
import { DISPLAY_CONDITIONS_ACTIONS } from '../utils/consts';

type DisplayConditionsAction = 'add' | 'append-to-all' | 'remove';

interface DisplayConditionsEditViewProps {
  onBack: () => void;
  onClose: () => void;
  onPreviewChanges: (data: DisplayConditionsFormData) => void;
  selectedMethods: SelectedMethodInfo[];
}

const ACTION_OPTIONS: RadioButtonOption[] = [
  {
    label: 'Add Display Conditions',
    value: DISPLAY_CONDITIONS_ACTIONS.ADD,
    description: 'Add conditions to all selected methods',
  },
  {
    label: 'Append to all groups',
    value: DISPLAY_CONDITIONS_ACTIONS.APPEND_TO_ALL,
    description: 'Append conditions to every existing group (AND logic)',
  },
  {
    label: 'Remove Display Conditions',
    value: DISPLAY_CONDITIONS_ACTIONS.REMOVE,
    description: 'Remove specific conditions from all selected methods',
  },
];

export const DisplayConditionsEditView: React.FC<DisplayConditionsEditViewProps> = ({
  onBack,
  onClose,
  onPreviewChanges,
  selectedMethods,
}) => {
  const { emitAngularAction } = useAppContext();
  const [action, setAction] = useState<DisplayConditionsAction>(DISPLAY_CONDITIONS_ACTIONS.ADD);
  const [conditionGroups, setConditionGroups] = useState<ConditionGroup[]>([]);
  const [selectedConditionsToRemove, setSelectedConditionsToRemove] = useState<string[]>([]);

  const { aggregatedConditions, hasConditions } = useConditionsAggregation(selectedMethods);

  const handleActionChange = (option: RadioButtonOption) => {
    setAction(option.value as DisplayConditionsAction);
    setConditionGroups([]);
    setSelectedConditionsToRemove([]);
  };

  const handleConditionToggle = (key: string) => {
    setSelectedConditionsToRemove((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const hasValidConditions = useMemo(() => {
    if (action === DISPLAY_CONDITIONS_ACTIONS.ADD || action === DISPLAY_CONDITIONS_ACTIONS.APPEND_TO_ALL) {
      return (
        conditionGroups.length > 0 &&
        conditionGroups.some((group) => group.conditions.some((c) => c.name && c.condition && c.values))
      );
    }
    return selectedConditionsToRemove.length > 0;
  }, [action, conditionGroups, selectedConditionsToRemove]);

  const handleOpenConditionDialog = () => {
    emitAngularAction({
      action: 'open-bulk-edit-condition-dialog',
      payload: {
        existingConditionGroups: conditionGroups.length > 0 ? conditionGroups : undefined,
        hideAddGroupButton: action === DISPLAY_CONDITIONS_ACTIONS.APPEND_TO_ALL,
      },
      callback: (result) => {
        if (result && result.conditionGroups && result.conditionGroups.length > 0) {
          const newGroups: ConditionGroup[] = result.conditionGroups.map((group: DialogConditionGroup) => ({
            conditions: group.conditions.map((c: DialogCondition) => ({
              name: c.name,
              displayName: c.displayName,
              condition: c.condition,
              values: c.values,
              weightUnit: c.weightUnit,
              optionalValue: c.optionalValue,
            })),
          }));
          setConditionGroups(newGroups);
        }
      },
    });
  };

  const handlePreviewChanges = () => {
    onPreviewChanges({
      action,
      conditionGroups:
        action === DISPLAY_CONDITIONS_ACTIONS.ADD || action === DISPLAY_CONDITIONS_ACTIONS.APPEND_TO_ALL
          ? conditionGroups
          : [],
      conditionsToRemove: action === DISPLAY_CONDITIONS_ACTIONS.REMOVE ? selectedConditionsToRemove : [],
    });
  };

  const getDisabledTooltipMessage = (): string => {
    if (action === DISPLAY_CONDITIONS_ACTIONS.ADD || action === DISPLAY_CONDITIONS_ACTIONS.APPEND_TO_ALL) {
      return 'Add conditions to preview changes';
    }
    return 'Select conditions to remove';
  };

  const getConditionsSummary = () => {
    if (conditionGroups.length === 0) return null;

    const allConditions = conditionGroups.flatMap((g) => g.conditions);
    if (allConditions.length === 0) return null;

    return (
      <div className="bulk-edit-modal__selected-conditions">
        <span>
          {allConditions.length} condition{allConditions.length !== 1 ? 's' : ''} to add
        </span>
        <button
          className="bulk-edit-modal__selected-conditions__edit"
          onClick={handleOpenConditionDialog}
          type="button">
          Edit
        </button>
      </div>
    );
  };

  return (
    <div className="bulk-edit-modal__view">
      <div className="bulk-edit-modal__header">
        <div className="bulk-edit-modal__header__left">
          <Button icon="back" variant="ghost" size="small" onClick={onBack} />
          <h2>Display conditions</h2>
        </div>
        <Button icon="close" variant="ghost" size="small" onClick={onClose} />
      </div>
      <div className="bulk-edit-modal__content">
        <div className="bulk-edit-modal__field">
          <label className="bulk-edit-modal__label">What Do You Want To Do?</label>
          <RadioButtonGroup
            options={ACTION_OPTIONS}
            defaultOptionValue={DISPLAY_CONDITIONS_ACTIONS.ADD}
            onChange={handleActionChange}
            disabled={false}
          />
        </div>

        {action === DISPLAY_CONDITIONS_ACTIONS.ADD && (
          <div className="bulk-edit-modal__field">
            {conditionGroups.length === 0 ? (
              <Button text="+ Add Conditions" onClick={handleOpenConditionDialog} size="medium" variant="secondary" />
            ) : (
              getConditionsSummary()
            )}
            <Divider />
            <div className="bulk-edit-modal__alert bulk-edit-modal__alert--info bulk-edit-modal__alert--no-border">
              <i className="icon icon-more_information" />
              <span>Conditions will be added as a new OR group alongside existing conditions.</span>
            </div>
          </div>
        )}

        {action === DISPLAY_CONDITIONS_ACTIONS.APPEND_TO_ALL && (
          <div className="bulk-edit-modal__field">
            {conditionGroups.length === 0 ? (
              <Button text="+ Add Conditions" onClick={handleOpenConditionDialog} size="medium" variant="secondary" />
            ) : (
              getConditionsSummary()
            )}
            <Divider />
            <div className="bulk-edit-modal__alert bulk-edit-modal__alert--info bulk-edit-modal__alert--no-border">
              <i className="icon icon-more_information" />
              <span>Conditions will be appended to every existing group using AND logic.</span>
            </div>
          </div>
        )}

        {action === DISPLAY_CONDITIONS_ACTIONS.REMOVE && (
          <div className="bulk-edit-modal__field">
            <label className="bulk-edit-modal__label">Select Conditions To Remove</label>
            {hasConditions ? (
              <div className="bulk-edit-modal__checkbox-list">
                {aggregatedConditions.map((condition) => (
                  <label key={condition.key} className="bulk-edit-modal__checkbox-item">
                    <Checkbox
                      checked={selectedConditionsToRemove.includes(condition.key)}
                      onChange={() => handleConditionToggle(condition.key)}
                    />
                    <span className="bulk-edit-modal__checkbox-item__text">{condition.displayText}</span>
                    <span className="bulk-edit-modal__checkbox-item__count">
                      {condition.methodCount} method{condition.methodCount !== 1 ? 's' : ''}
                    </span>
                  </label>
                ))}
              </div>
            ) : (
              <span className="bulk-edit-modal__hint">No conditions found in selected methods.</span>
            )}
            {selectedConditionsToRemove.length > 0 && (
              <>
                <Divider />
                <div className="bulk-edit-modal__alert bulk-edit-modal__alert--warning bulk-edit-modal__alert--no-border">
                  <i className="icon icon-warning" />
                  <span>
                    {selectedConditionsToRemove.length} condition{selectedConditionsToRemove.length !== 1 ? 's' : ''}{' '}
                    will be removed from the selected methods
                  </span>
                </div>
              </>
            )}
          </div>
        )}
      </div>
      <div className="bulk-edit-modal__footer">
        <Button text="Back" onClick={onBack} size="medium" variant="secondary" />
        {!hasValidConditions ? (
          <Tooltip tooltipText={getDisabledTooltipMessage()} maxWidth={220}>
            <Button text="Preview Changes" onClick={handlePreviewChanges} size="medium" variant="primary" disabled />
          </Tooltip>
        ) : (
          <div className="bulk-edit-modal__button-wrapper">
            <Button text="Preview Changes" onClick={handlePreviewChanges} size="medium" variant="primary" />
          </div>
        )}
      </div>
    </div>
  );
};
